import { __awaiter } from "tslib";
import { Operation } from 'slate';
import { EditorService } from './editor';
import { Plugin } from './plugin';
import { VeltService } from './velt';
export { SlateVeltComment } from './SlateVeltComment';
export * from './types';
// Plugin configuration
export const withVeltComments = (editor, options) => {
    const { isInline, isVoid, onChange } = editor;
    if (options === null || options === void 0 ? void 0 : options.HistoryEditor) {
        EditorService.setHistoryEditor(options.HistoryEditor);
    }
    // Override isInline to treat Velt comment text elements as inline elements
    // This ensures comment text flows naturally within the document
    editor.isInline = (element) => {
        return EditorService.isVeltCommentText(element) ? true : isInline(element);
    };
    // Override isVoid to ensure Velt comment text elements are not void elements
    // This allows comment text to contain and display content
    editor.isVoid = (element) => {
        return EditorService.isVeltCommentText(element) ? false : isVoid(element);
    };
    // Add observer for value changes to track and update comments when text changes
    editor.onChange = () => {
        // Call the original onChange if it exists to preserve existing behavior
        if (onChange) {
            onChange();
        }
        // Only process text-related operations to avoid unnecessary updates
        const isTextChange = editor.operations.some((operation) => Operation.isTextOperation(operation));
        if (isTextChange) {
            // Update comment positions and states when text content changes
            onSlateValueChange({ editor });
        }
    };
    // Return the enhanced editor with Velt comments functionality
    return editor;
};
export const triggerAddComment = (editor, config) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        return addComment({ editor, context: config === null || config === void 0 ? void 0 : config.context });
    }
    catch (error) {
        VeltService.catchError('Error triggering add comment', error);
    }
});
/**
 * Creates a comment annotation for the currently selected text in the editor
 * This function handles the entire flow from getting the selected text to creating
 * the actual comment through the Velt service
 */
export const addComment = (_a) => __awaiter(void 0, [_a], void 0, function* ({ editorId, editor, context: clientContext, }) {
    var _b, _c;
    try {
        // get comment element from Velt sdk
        const commentElement = VeltService.getCommentElement();
        if (!commentElement)
            return;
        // Ensure we have an editor ID for proper tracking
        if (!editorId) {
            editorId = (_b = EditorService.getEditorId(editor)) !== null && _b !== void 0 ? _b : undefined;
        }
        // Initialize plugin instance with editor context
        const plugin = Plugin.getInstance({ editorId, editor });
        // Get text currently selected by the user
        const selectedText = plugin.editorService.getSelectedText(editor);
        // Exit if no text is selected - nothing to comment on
        if (!selectedText) {
            return;
        }
        // Find the parent node containing the selection to establish position context
        const targetTextNode = plugin.editorService.findParentNodeWithIdForSelection(editor);
        // Extract node ID and location information for proper positioning
        let targetTextNodeId = undefined;
        let locationId = null;
        if (targetTextNode) {
            targetTextNodeId = targetTextNode.id;
            // Find the closest parent with a location ID to properly anchor the comment
            locationId = (_c = targetTextNode
                .closest('[data-velt-location-id]')) === null || _c === void 0 ? void 0 : _c.getAttribute('data-velt-location-id');
        }
        const occurrence = plugin.editorService.findOccurrenceOfSelectedText(editor, targetTextNode !== null && targetTextNode !== void 0 ? targetTextNode : undefined);
        // Exit if we can't determine occurrence information
        if (!occurrence) {
            return;
        }
        // Initialize context object for the comment
        let context = {};
        if (clientContext && typeof clientContext === 'object') {
            context = Object.assign(Object.assign({}, context), clientContext);
        }
        // Add text editor specific configuration to context
        // This helps position and track the comment as document changes
        context.textEditorConfig = {
            text: selectedText,
            occurrence: occurrence,
            targetTextNodeId: targetTextNodeId,
        };
        if (editorId) {
            context.textEditorConfig.editorId = editorId;
        }
        // Short delay to prevent comment dialog from closing immediately
        // This improves user experience when interacting with comments
        yield new Promise((resolve) => setTimeout(resolve, 200));
        // Create location reference if we have a location ID
        let location;
        if (locationId) {
            location = { id: locationId };
        }
        try {
            // Create the actual comment through Velt service
            VeltService.addManualComment({ context, location });
        }
        catch (error) {
            VeltService.catchError('Error creating comment', error);
        }
    }
    catch (error) {
        VeltService.catchError('Error adding comment', error);
    }
});
export function highlightComments(editor, commentAnnotations, editorId) {
    try {
        renderComments({ editor, editorId, commentAnnotations });
    }
    catch (error) {
        VeltService.catchError('Error highlighting tiptap-velt-comments', error);
    }
}
export const renderComments = ({ editor, editorId, commentAnnotations }) => {
    var _a;
    try {
        if (editor) {
            if (!editorId) {
                editorId = (_a = EditorService.getEditorId(editor)) !== null && _a !== void 0 ? _a : undefined;
            }
            commentAnnotations = commentAnnotations === null || commentAnnotations === void 0 ? void 0 : commentAnnotations.filter((annotation) => {
                var _a, _b, _c;
                return !!((_a = annotation.context) === null || _a === void 0 ? void 0 : _a.textEditorConfig) &&
                    ((_c = (_b = annotation.context) === null || _b === void 0 ? void 0 : _b.textEditorConfig) === null || _c === void 0 ? void 0 : _c.editorId) === editorId;
            });
            const plugin = Plugin.getInstance({ editorId, editor });
            plugin.storeService.updateGlobalStore({ editor, comments: commentAnnotations });
            VeltService.subscribeToSelectedAnnotationsMapSingleton();
        }
    }
    catch (error) {
        VeltService.catchError('Error highlighting tiptap-velt-comments', error);
    }
};
export const onSlateValueChange = ({ editor, editorId, }) => {
    var _a;
    try {
        if (!editorId) {
            editorId = (_a = EditorService.getEditorId(editor)) !== null && _a !== void 0 ? _a : undefined;
        }
        const plugin = Plugin.getInstance({ editorId, editor });
        // Get the current editor text content
        const currentEditorTextContent = plugin.editorService.getEditorTextContent(editor);
        const previousEditorTextContent = plugin.getPreviousEditorTextContent();
        // Prevent unnecessary processing if content hasn't changed
        if (currentEditorTextContent === previousEditorTextContent)
            return;
        // Update the previous content for next comparison
        plugin.setPreviousEditorTextContent(currentEditorTextContent);
        plugin.editorService.onSlateValueChange({ editor });
    }
    catch (error) {
        VeltService.catchError('Error in processVeltCommentChanges:', error);
    }
};
